import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import translations from './i18n';
import api from './api';

const LangContext = createContext(null);

export function LangProvider({ children }) {
  const [lang, setLang] = useState(() => {
    return localStorage.getItem('winluxor_lang') || 'en';
  });

  // Inline editing mode (admin-only). When true, components display pencil icons
  // next to editable texts. Persisted in localStorage so it survives navigation.
  const [editMode, setEditMode] = useState(() => {
    return localStorage.getItem('winluxor_edit_mode') === 'true';
  });

  // Text overrides loaded from /api/text-overrides. Shape: { key: {fr, en} }
  const [overrides, setOverrides] = useState({});
  const [overridesLoaded, setOverridesLoaded] = useState(false);

  // Load overrides on mount + whenever explicitly refreshed
  const refreshOverrides = useCallback(async () => {
    try {
      const res = await api.get('/api/text-overrides');
      setOverrides(res.data?.overrides || {});
    } catch {
      setOverrides({});
    } finally {
      setOverridesLoaded(true);
    }
  }, []);

  useEffect(() => {
    refreshOverrides();
  }, [refreshOverrides]);

  const switchLang = useCallback((newLang) => {
    setLang(newLang);
    localStorage.setItem('winluxor_lang', newLang);
  }, []);

  const toggleEditMode = useCallback(() => {
    setEditMode((prev) => {
      const next = !prev;
      localStorage.setItem('winluxor_edit_mode', String(next));
      return next;
    });
  }, []);

  // Translation lookup: 1) override (current lang) 2) override (other lang) 3) i18n in current lang 4) english fallback 5) raw key
  const t = useCallback((key) => {
    if (key && overrides[key]) {
      const ov = overrides[key];
      const val = ov[lang];
      if (val && val.trim().length > 0) return val;
      // Cross-language fallback: if current lang override is empty but the
      // other language override has content, use that (so a single-lang edit
      // is visible everywhere instead of silently disappearing).
      const otherLang = lang === 'fr' ? 'en' : 'fr';
      const otherVal = ov[otherLang];
      if (otherVal && otherVal.trim().length > 0) return otherVal;
    }
    return translations[lang]?.[key] || translations['en']?.[key] || key;
  }, [lang, overrides]);

  // Optimistically update an override locally (so UI refreshes instantly after admin save)
  const setOverrideLocal = useCallback((key, valueFr, valueEn) => {
    setOverrides((prev) => ({
      ...prev,
      [key]: { fr: valueFr || '', en: valueEn || '' },
    }));
  }, []);

  // Get current default value (from i18n.js) for a given key — used in edit dialogs
  const getDefault = useCallback((key, targetLang = null) => {
    const l = targetLang || lang;
    return translations[l]?.[key] || translations['en']?.[key] || '';
  }, [lang]);

  return (
    <LangContext.Provider
      value={{
        lang,
        switchLang,
        t,
        editMode,
        toggleEditMode,
        overrides,
        overridesLoaded,
        refreshOverrides,
        setOverrideLocal,
        getDefault,
      }}
    >
      {children}
    </LangContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error('useLang must be used within LangProvider');
  return ctx;
}
